import * as borogove from "borogove";
import DOMPurify from "dompurify";
import { Button, Message } from "@chatscope/chat-ui-kit-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router";
import { ReactNode, useRef, useEffect } from "react";
import {
	faCheck,
	faCheckDouble,
	faCircleExclamation,
	faClock,
	faPencil,
	faReply,
} from "@fortawesome/free-solid-svg-icons";

function longDateTimeOnlyToday(date: Date) {
	const today =
		new Date().toLocaleString([], { dateStyle: "short" }) ===
		date.toLocaleString([], { dateStyle: "short" });
	return date.toLocaleString([], {
		dateStyle: today ? undefined : "long",
		timeStyle: "short",
	});
}

function shortDateTimeOnlyToday(date: Date) {
	const today =
		new Date().toLocaleString([], { dateStyle: "short" }) ===
		date.toLocaleString([], { dateStyle: "short" });
	return date.toLocaleString([], {
		dateStyle: today ? undefined : "short",
		timeStyle: "short",
	});
}

const renderReaction = (reaction: borogove.Reaction) => {
	return reaction.render<JSX.Element | string>(
		(text: string) => text,
		(alt: string, uri: string) => (
			<img crossOrigin="anonymous" alt={alt} src={uri} />
		),
	);
};

export const attachmentContent = (
	attachment: borogove.ChatAttachment,
	size?: string,
) => {
	if (attachment?.mime?.indexOf("image/") === 0) {
		return (
			<a href={attachment.uris[0]}>
				<img
					alt={attachment.name || "attachment"}
					crossOrigin="anonymous"
					src={attachment.uris[0]}
					style={{ height: size || "15em", objectFit: "scale-down" }}
				/>
			</a>
		);
	} else if (attachment?.mime?.indexOf("audio/") === 0) {
		return (
			<audio crossOrigin="anonymous" src={attachment.uris[0]} controls></audio>
		);
	} else if (attachment?.mime?.indexOf("video/") === 0) {
		return (
			<video
				crossOrigin="anonymous"
				src={attachment.uris[0]}
				style={{ height: size || "15em", objectFit: "scale-down" }}
				controls
			></video>
		);
	} else {
		return <a href={attachment.uris[0]}>attachment</a>;
	}
};

const linkMeta = (meta: borogove.LinkMetadata) => {
	const image = meta.image.find((image) => image.match(/^(data|https):/));

	return (
		<a
			className="h-item"
			href={meta.url}
			title={"Description of " + meta.about}
		>
			{image && <img className="u-photo" src={image} alt="Image of link" />}
			{meta.title && <div className="p-name">{meta.title}</div>}
			<div className="u-url">{meta.url}</div>
			{meta.description && <div className="p-summary">{meta.description}</div>}
		</a>
	);
};

const formatMention = (dom: Node, name: string) => {
	const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const regex = new RegExp(`(\\b${escapeRegExp(name)}\\b)`, "g");

	const walker = document.createTreeWalker(dom, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => {
			if (regex.test(node?.nodeValue ?? "")) {
				return NodeFilter.FILTER_ACCEPT;
			}
			return NodeFilter.FILTER_REJECT;
		},
	});

	while (walker.nextNode()) {
		const textNode = walker.currentNode;
		if (!textNode?.nodeValue) continue;

		const parts = textNode.nodeValue.split(regex);

		const fragment = document.createDocumentFragment();
		for (const [index, part] of parts.entries()) {
			if (!part) continue;

			// Odd indices are the matched names (due to capture group)
			if (index % 2 === 1) {
				const span = document.createElement("span");
				span.className = "self";
				span.textContent = part;
				fragment.appendChild(span);
			} else {
				fragment.appendChild(document.createTextNode(part));
			}
		}

		textNode.parentNode?.replaceChild(fragment, textNode);
	}

	return dom;
};

export const MessageBubble = ({
	chat,
	client,
	sender,
	parentSender,
	m,
	position,
	setSelectedThread,
	className,
	children,
}: {
	chat: borogove.Chat;
	client: borogove.Client;
	sender: borogove.Participant;
	parentSender: borogove.Participant | null;
	m: borogove.ChatMessage;
	position: "first" | "last" | "single" | "normal";
	setSelectedThread: (id: string | null) => void;
	className?: string;
	children: ReactNode;
}) => {
	const navigate = useNavigate();

	const toggleReaction = (
		m: borogove.ChatMessage,
		reaction: borogove.Reaction,
	) => {
		const myReaction = (m.reactions.get(reaction.key) || []).find(
			(r: borogove.Reaction) => chat.getParticipantDetails(r.senderId).isSelf,
		);
		if (myReaction) {
			chat.removeReaction(m, myReaction);
		} else {
			chat.addReaction(m, reaction);
		}
	};

	const contentRef = useRef<HTMLDivElement | null>(null);
	const parentContentRef = useRef<HTMLDivElement | null>(null);
	const bodyLength = m
		.html()
		.reduce((txt, isTag, _, kids: number[] | null) =>
			isTag ? (kids ?? []).reduce((x, y) => x + y, 0) : txt.length,
		)
		.reduce((x, y) => x + y, 0);
	const parentMessage = m.replyToMessage;
	const reactionEntries = [...(m.reactions.entries() || [])];
	const shortDateTimeHuman = shortDateTimeOnlyToday(new Date(m.timestamp));
	const longDateTimeHuman = longDateTimeOnlyToday(new Date(m.timestamp));
	const fullDateTimeHuman = new Date(m.timestamp)
		.toLocaleString([], {
			dateStyle: "full",
			timeStyle: "long",
		})
		.replace(/\s*\b[A-Z]{2,4}$/, ""); // remove trailing TZ abbrev

	useEffect(() => {
		if (!contentRef.current) return;

		const html = m.html(sender).asDOM();
		DOMPurify.sanitize(formatMention(html, client.displayName()));

		contentRef.current.innerHTML = "";
		contentRef.current.appendChild(html);

		if (!parentContentRef.current) return;

		const parentHtml = parentMessage?.html()?.asDOM();
		if (parentHtml) {
			DOMPurify.sanitize(parentHtml);

			parentContentRef.current.innerHTML = "";
			parentContentRef.current.appendChild(parentHtml);
		}
	}, [m, parentMessage, client, sender]);

	return (
		<Message
			model={{
				direction: m.isIncoming() ? "incoming" : "outgoing",
				position,
				sender: sender.displayName,
				sentTime: longDateTimeHuman,
			}}
			onClick={() => setSelectedThread(m.threadId)}
			id={`message:${m.serverId || m.localId}`}
			data-server-id={m.serverId}
			data-local-id={m.localId}
			className={
				(bodyLength < 25 && !m.replyToMessage ? "small " : "") +
				(m.type === borogove.MessageType.MessageChannelPrivate
					? "invite"
					: "") +
				" " +
				className
			}
		>
			{children}
			<Message.CustomContent>
				<div title={fullDateTimeHuman}>
					{parentMessage && (
						<>
							<Button
								title="Jump to parent"
								icon={<FontAwesomeIcon icon={faReply} />}
								onClick={() => navigate(`#message:${parentMessage.serverId}`)}
							>
								{parentSender?.displayName}
							</Button>
							<blockquote className="u-in-reply-to">
								<div
									className="cs-message__html-content"
									ref={parentContentRef}
								/>
								{m.replyToMessage.attachments.map((attachment) =>
									attachmentContent(attachment),
								)}
							</blockquote>
						</>
					)}
					<div className="cs-message__html-content" ref={contentRef} />
					{m.moderationReason() && <div className="moderated">{m.moderationReason()}</div>}
					{m.linkMetadata.map((meta) => linkMeta(meta))}
					{m.attachments.map((attachment) => attachmentContent(attachment))}
					<div className="meta">
						<div>
							{m.threadId && (
								<img
									src={m.threadIcon() ?? undefined}
									alt={"thread " + m.threadId.substring(0, 5)}
									className="thread"
								/>
							)}
							{m.timestamp && ["last", "single"].includes(position) && (
								<time dateTime={m.timestamp} title={fullDateTimeHuman}>
									{shortDateTimeHuman}
								</time>
							)}
							{m.versions.length > 0 && (
								<FontAwesomeIcon
									icon={faPencil}
									title={"Edited " + (m.versions.length - 1) + " times"}
								/>
							)}
							{!m.isIncoming() &&
								m.status === borogove.MessageStatus.MessagePending && (
									<FontAwesomeIcon
										icon={faClock}
										title={m.statusText ?? "Sending..."}
									/>
								)}
							{!m.isIncoming() &&
								m.status === borogove.MessageStatus.MessageFailedToSend && (
									<FontAwesomeIcon
										icon={faCircleExclamation}
										title={m.statusText ?? "Error"}
									/>
								)}
							{!m.isIncoming() &&
								m.status ===
									borogove.MessageStatus.MessageDeliveredToServer && (
									<FontAwesomeIcon
										icon={faCheck}
										title={m.statusText ?? "Sent"}
									/>
								)}
							{!m.isIncoming() &&
								m.status ===
									borogove.MessageStatus.MessageDeliveredToDevice && (
									<FontAwesomeIcon
										icon={faCheckDouble}
										title={m.statusText ?? "Received"}
									/>
								)}
						</div>

						{(chat.getParticipants().length > 2 ||
							m.senderId !== chat.chatId) &&
							["last", "single"].includes(position) &&
							!m.moderationReason() &&
							m.isIncoming() && (
								<div className="h-card sender">{sender?.displayName}</div>
							)}
					</div>
				</div>
			</Message.CustomContent>
			{reactionEntries.length > 0 && (
				<Message.Footer className="reactions">
					{reactionEntries.map(([key, reactions]) => (
						<Button
							border
							onClick={() =>
								toggleReaction(
									m,
									reactions.find(
										(r) => chat.getParticipantDetails(r.senderId).isSelf,
									) || reactions[0],
								)
							}
							key={key}
							className={
								reactions.find(
									(r) => chat.getParticipantDetails(r.senderId).isSelf,
								)
									? "reaction mine"
									: "reaction"
							}
							title={reactions
								.map(
									(r) =>
										chat.getParticipantDetails(r.senderId).displayName +
										" @ " +
										shortDateTimeOnlyToday(new Date(r.timestamp)),
								)
								.join("\n")}
						>
							{renderReaction(reactions[0])} {" " + reactions.length}
						</Button>
					))}
				</Message.Footer>
			)}
		</Message>
	);
};
